import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  searchPermits,
  getPermitById,
  calculatePermitFees,
  calculatePermitFeesNew,
  getAvailableStates,
  getCitiesForState,
  searchCities,
  getScopeCategories,
  getRecentSearches,
  getPermitHistory,
  getSavedSearches,
  savePermitSearch,
  removeSavedSearch,
  exportPermitPDF,
  sharePermitByEmail,
} from '@/services/permits.service';
import { queryKeys } from '@/lib/queryClient';
import type {
  PermitSearchRequest,
  FeeCalculationRequest,
  PermitCalculationRequest,
} from '@/types/api.types';

/**
 * Hook for permit search mutation
 */
export const usePermitSearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (searchParams: PermitSearchRequest) => searchPermits(searchParams),
    onSuccess: (data, variables) => {
      // Cache the search result
      queryClient.setQueryData(queryKeys.permits.search(variables), data);

      // Invalidate recent searches to include new search
      queryClient.invalidateQueries({ queryKey: queryKeys.permits.all });
    },
    onError: (error) => {
      console.error('Permit search failed:', error);
    },
  });
};

/**
 * Hook to get permit details by ID
 */
export const usePermitDetails = (permitId: string, enabled = true) => {
  return useQuery({
    queryKey: queryKeys.permits.detail(permitId),
    queryFn: () => getPermitById(permitId),
    enabled: enabled && !!permitId,
  });
};

/**
 * Hook for fee calculation mutation
 */
export const useFeeCalculation = (permitId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (calculation: FeeCalculationRequest) =>
      calculatePermitFees(permitId, calculation),
    onSuccess: (data, variables) => {
      // Cache the calculation result
      queryClient.setQueryData(
        queryKeys.permits.fees(permitId, variables.job_details),
        data
      );
    },
    onError: (error) => {
      console.error('Fee calculation failed:', error);
    },
  });
};

/**
 * Hook to get recent permit searches
 */
export const useRecentSearches = (limit = 10) => {
  return useQuery({
    queryKey: ['permits', 'recent', limit],
    queryFn: () => getRecentSearches(limit),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * Hook to get paginated permit search history
 */
export const usePermitHistory = (page = 1, pageSize = 20) => {
  const offset = (page - 1) * pageSize;
  return useQuery({
    queryKey: ['permits', 'history', page, pageSize],
    queryFn: () => getPermitHistory(pageSize, offset),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * Hook to get saved permit searches
 */
export const useSavedSearches = () => {
  return useQuery({
    queryKey: ['permits', 'saved'],
    queryFn: getSavedSearches,
  });
};

/**
 * Hook to save a permit search
 */
export const useSavePermitSearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (permitId: string) => savePermitSearch(permitId),
    onSuccess: () => {
      // Invalidate saved searches to refresh the list
      queryClient.invalidateQueries({ queryKey: ['permits', 'saved'] });
    },
    onError: (error) => {
      console.error('Failed to save permit search:', error);
    },
  });
};

/**
 * Hook to remove a saved permit search
 */
export const useRemoveSavedSearch = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (permitId: string) => removeSavedSearch(permitId),
    onSuccess: () => {
      // Invalidate saved searches to refresh the list
      queryClient.invalidateQueries({ queryKey: ['permits', 'saved'] });
    },
    onError: (error) => {
      console.error('Failed to remove saved search:', error);
    },
  });
};

/**
 * Hook to export permit as PDF
 */
export const useExportPermitPDF = () => {
  return useMutation({
    mutationFn: (permitId: string) => exportPermitPDF(permitId),
    onSuccess: (blob, permitId) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `permit-${permitId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => {
      console.error('Failed to export permit:', error);
    },
  });
};

/**
 * Hook to share permit via email
 */
export const useSharePermit = () => {
  return useMutation({
    mutationFn: ({ permitId, email }: { permitId: string; email: string }) =>
      sharePermitByEmail(permitId, email),
    onSuccess: () => {
      console.log('Permit shared successfully');
    },
    onError: (error) => {
      console.error('Failed to share permit:', error);
    },
  });
};

/**
 * Hook to get available states
 */
export const useAvailableStates = () => {
  return useQuery({
    queryKey: ['permits', 'states'],
    queryFn: getAvailableStates,
    staleTime: 1000 * 60 * 60, // 1 hour - states rarely change
  });
};

/**
 * Hook to get cities for a state
 */
export const useCitiesForState = (state: string, enabled = true) => {
  return useQuery({
    queryKey: ['permits', 'cities', state],
    queryFn: () => getCitiesForState(state),
    enabled: enabled && !!state && state.length === 2,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Hook to search cities within a state
 */
export const useSearchCities = (state: string, query: string, enabled = true) => {
  return useQuery({
    queryKey: ['permits', 'cities', 'search', state, query],
    queryFn: () => searchCities(state, query),
    enabled: enabled && !!state && state.length === 2,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to get scope categories
 */
export const useScopeCategories = () => {
  return useQuery({
    queryKey: ['permits', 'scope-categories'],
    queryFn: getScopeCategories,
    staleTime: 1000 * 60 * 60, // 1 hour - categories rarely change
  });
};

/**
 * Hook for permit fee calculation (new API)
 */
export const useCalculatePermitFees = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: PermitCalculationRequest) => calculatePermitFeesNew(request),
    onSuccess: (data, variables) => {
      // Cache the result
      queryClient.setQueryData(
        ['permits', 'calculation', variables.city, variables.state, variables.scope_category],
        data
      );
      // Invalidate recent searches to include new search
      queryClient.invalidateQueries({ queryKey: ['permits', 'recent'] });
    },
    onError: (error) => {
      console.error('Fee calculation failed:', error);
    },
  });
};
